import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import Header from './components/Header';
import Footer from './components/Footer';
import DropZone from './components/DropZone';
import ProgressBar from './components/ProgressBar';
import CompressionStats from './components/CompressionStats';
import Button from './components/Button';
import CookieBanner from './components/CookieBanner';

import { readArchive } from './utils/archiveReader';
import type { ArchiveEntry } from './utils/archiveReader';
import { compressImages } from './utils/imageCompressor';
import type { CompressionResult } from './utils/imageCompressor';
import { isImage, formatBytes } from './utils/constants';
import { LanguageProvider, useTranslation } from './i18n/LanguageContext';

type Stage = 'idle' | 'reading' | 'ready' | 'compressing' | 'packing' | 'done';

function HomePage() {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>('idle');
  const [fileName, setFileName] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<CompressionResult[]>([]);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [targetRatio, setTargetRatio] = useState(0.4);
  const [imageEntries, setImageEntries] = useState<ArchiveEntry[]>([]);
  const [nonImageEntries, setNonImageEntries] = useState<ArchiveEntry[]>([]);
  const [totalOriginalSize, setTotalOriginalSize] = useState(0);

  const handleFiles = useCallback(async (files: File[]) => {
    setError('');
    setResults([]);
    setDownloadUrl('');

    const isArchive = files.length === 1 && /\.(zip|rar)$/i.test(files[0].name);

    if (isArchive) {
      setFileName(files[0].name);
      try {
        setStage('reading');
        const entries = await readArchive(files[0]);

        const images = entries.filter((e) => isImage(e.path));
        const nonImages = entries.filter((e) => !isImage(e.path));

        if (images.length === 0) {
          setError(t.noImagesFound);
          setStage('idle');
          return;
        }

        const totalSize = images.reduce((s, e) => s + e.data.size, 0);
        setImageEntries(images);
        setNonImageEntries(nonImages);
        setTotalOriginalSize(totalSize);
        setStage('ready');
      } catch (err) {
        setError(`${t.errorPrefix}: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setStage('idle');
      }
    } else {
      const imageFiles = files.filter((f) => isImage(f.name));
      if (imageFiles.length === 0) {
        setError(t.noImagesFound);
        return;
      }

      setFileName(imageFiles.length === 1 ? imageFiles[0].name : `${imageFiles.length} images`);
      const entries: ArchiveEntry[] = imageFiles.map((f) => ({ path: f.name, data: f }));
      const totalSize = entries.reduce((s, e) => s + e.data.size, 0);

      setImageEntries(entries);
      setNonImageEntries([]);
      setTotalOriginalSize(totalSize);
      setStage('ready');
    }
  }, [t]);

  const handleCompress = useCallback(async () => {
    try {
      setStage('compressing');
      setProgress({ current: 0, total: imageEntries.length });

      const compressionResults = await compressImages(imageEntries, targetRatio, (current, total) => {
        setProgress({ current, total });
      });

      setStage('packing');
      const zip = new JSZip();

      for (const result of compressionResults) {
        zip.file(result.path, result.data);
      }
      for (const entry of nonImageEntries) {
        zip.file(entry.path, entry.data);
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);

      setResults(compressionResults);
      setDownloadUrl(url);
      setStage('done');
    } catch (err) {
      setError(`${t.errorPrefix}: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setStage('idle');
    }
  }, [imageEntries, nonImageEntries, targetRatio, t]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = outputName;
    a.click();
  };

  const handleReset = () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setStage('idle');
    setFileName('');
    setProgress({ current: 0, total: 0 });
    setResults([]);
    setDownloadUrl('');
    setError('');
    setImageEntries([]);
    setNonImageEntries([]);
    setTotalOriginalSize(0);
  };

  const isProcessing = stage === 'reading' || stage === 'compressing' || stage === 'packing';
  const baseName = /\.(zip|rar)$/i.test(fileName) ? fileName.replace(/\.[^.]+$/, '') : 'images';
  const outputName = baseName + '_compressed.zip';

  return (
    <>
      <Header />
      <main className="main">
        <div className="section-gap">
          <DropZone onFilesSelected={handleFiles} disabled={isProcessing || stage === 'done'} />

          {error && (
            <div className="file-name" style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
              {error}
            </div>
          )}

          {stage === 'reading' && (
            <div className="file-name">{t.readingArchive}</div>
          )}

          {stage === 'ready' && (
            <>
              <div className="file-name">{fileName}</div>
              <div className="slider-container">
                <div className="slider-header">
                  <span className="slider-label">{t.compressionLevel}</span>
                  <span className="slider-value">{Math.round((1 - targetRatio) * 100)}%</span>
                </div>
                <input
                  type="range"
                  className="slider"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={targetRatio}
                  onChange={(e) => setTargetRatio(parseFloat(e.target.value))}
                />
                <div className="slider-footer">
                  <span>{t.estimatedSize}: ~{formatBytes(totalOriginalSize * targetRatio)}</span>
                </div>
              </div>
              <Button onClick={handleCompress}>{t.compress}</Button>
            </>
          )}

          {stage === 'compressing' && (
            <ProgressBar current={progress.current} total={progress.total} />
          )}

          {stage === 'packing' && (
            <div className="file-name">{t.packingArchive}</div>
          )}

          {stage === 'done' && results.length > 0 && (
            <>
              <button className="download-link" onClick={handleDownload}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {outputName}
              </button>
              <CompressionStats results={results} />
              <Button variant="secondary" onClick={handleReset}>{t.dropzoneText}</Button>
            </>
          )}
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <HomePage />
    </LanguageProvider>
  );
}
